import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, BarChart3, Zap, Shield, Globe, ArrowRight, Play, MessageCircle,
  X, Check, MessageSquare, FileText, CreditCard, Smartphone, ChevronRight
} from "lucide-react";

const WHATSAPP_NUMBER = "34681271020";
const WHATSAPP_MESSAGE = "Hola! Quiero información sobre VEXL.fit para lanzar mi plataforma fitness";

const openWhatsApp = () => {
  const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
  window.open(url, "_blank");
};

const LandingPage = () => {
  const navigate = useNavigate();

  // SEO Meta tags
  React.useEffect(() => {
    document.title = "VEXL.fit | Plataforma White-Label para Influencers Fitness";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "Lanza tu propia app de entrenamiento con tu marca. Plataforma B2B para influencers fitness: gestiona alumnos, crea rutinas, recibe pagos directos con Stripe.";
    
    const ogTags = [
      { property: "og:title", content: "VEXL.fit | Tu Imperio Fitness Digital" },
      { property: "og:description", content: "Plataforma white-label para influencers fitness. Lanza tu app, gestiona alumnos y monetiza tu comunidad." },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "/logo.png" }
    ];
    
    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.content = tag.content;
    });
    
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = "keywords";
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = "fitness app, influencer fitness, plataforma entrenamiento, white label fitness, coaching online, app personalizada fitness, stripe fitness";
    
  }, []);

  return (
    <div className="min-h-screen bg-vexl-blue overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="VEXL.fit" className="h-14 w-auto" />
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#problema" className="text-white/80 hover:text-white transition-colors text-sm">El Problema</a>
            <a href="#features" className="text-white/80 hover:text-white transition-colors text-sm">Features</a>
            <a href="#how" className="text-white/80 hover:text-white transition-colors text-sm">Cómo funciona</a>
          </nav>
          <button
            data-testid="admin-access-btn"
            onClick={() => navigate("/admin")}
            className="bg-white/10 backdrop-blur-sm text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-white/20 transition-all"
          >
            Admin Portal
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-20">
        {/* Grid Background */}
        <div className="absolute inset-0 grid-bg opacity-50" />
        
        {/* Floating Cards */}
        <div className="absolute top-1/4 left-[10%] animate-float hidden lg:block">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-red-500 mb-3" />
            <p className="text-white font-medium text-sm">chris.fit</p>
            <p className="text-white/60 text-xs">15k students</p>
          </div>
        </div>
        
        <div className="absolute bottom-1/3 right-[8%] animate-float-delayed hidden lg:block">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 mb-3" />
            <p className="text-white font-medium text-sm">maria.fit</p>
            <p className="text-white/60 text-xs">8.5k students</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 text-center max-w-6xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-vexl-lime text-vexl-blue px-4 py-2 rounded-full text-sm font-semibold mb-8">
            <Zap className="w-4 h-4" />
            White-Label Fitness Platform
          </div>

          {/* Main Headline */}
          <h1 className="text-display-xl text-white mb-6">
            <span className="text-vexl-lime">#</span>FITNESS
            <br />
            <span className="text-white/90">EMPIRE</span>
            <br />
            <span className="text-white/80">BUILDER</span>
          </h1>

          <p className="text-xl text-white/70 max-w-2xl mx-auto mb-10">
            Lanza tu propia app de entrenamiento con tu marca. 
            Gestiona alumnos, crea rutinas y monetiza tu comunidad fitness.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              data-testid="get-started-btn"
              onClick={openWhatsApp}
              className="group bg-vexl-lime text-vexl-blue font-bold px-8 py-4 rounded-full text-lg flex items-center gap-2 hover:shadow-glow-lg transition-all hover:scale-105"
            >
              <MessageCircle className="w-5 h-5" />
              Contactar por WhatsApp
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button 
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Play className="w-5 h-5 text-vexl-lime" fill="currentColor" />
              </div>
              <span>Ver Demo</span>
            </button>
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-12 mt-16">
            <div className="text-center">
              <p className="font-display text-4xl font-bold text-vexl-lime">500+</p>
              <p className="text-white/60 text-sm">Influencers</p>
            </div>
            <div className="text-center">
              <p className="font-display text-4xl font-bold text-vexl-lime">50k+</p>
              <p className="text-white/60 text-sm">Students</p>
            </div>
            <div className="text-center">
              <p className="font-display text-4xl font-bold text-vexl-lime">$2M+</p>
              <p className="text-white/60 text-sm">Revenue Generated</p>
            </div>
          </div>
        </div>

        {/* Rotating Badge */}
        <div className="absolute bottom-10 right-10 hidden lg:block">
          <div className="relative w-28 h-28">
            <div className="absolute inset-0 bg-vexl-lime rounded-full flex items-center justify-center animate-spin-slow">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <defs>
                  <path id="circle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
                </defs>
                <text className="text-[10px] font-bold fill-vexl-blue uppercase tracking-[0.3em]">
                  <textPath href="#circle">
                    EMPIEZA HOY • EMPIEZA HOY •
                  </textPath>
                </text>
              </svg>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <ArrowRight className="w-8 h-8 text-vexl-blue" />
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEMA vs SOLUCION Section */}
      <section id="problema" className="py-24 px-6 bg-vexl-bg relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-red-500/20 text-red-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              El Problema
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mb-4">
              ¿Te suena familiar?
            </h2>
            <p className="text-xl text-vexl-text-secondary max-w-2xl mx-auto">
              Gestionas tu negocio fitness con mil herramientas diferentes
            </p>
          </div>

          {/* Before/After Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {/* ANTES - El Caos */}
            <div className="relative">
              <div className="absolute -top-4 left-4 bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold z-10">
                ANTES
              </div>
              <div className="bg-vexl-surface border-2 border-red-500/30 rounded-3xl p-8 h-full">
                <div className="space-y-6">
                  {/* WhatsApp Chaos */}
                  <div className="flex items-start gap-4 p-4 bg-red-500/10 rounded-2xl border border-red-500/20">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">+500 chats de WhatsApp</h4>
                      <p className="text-sm text-vexl-text-secondary">Mensajes perdidos, rutinas enviadas mil veces, clientes olvidados...</p>
                      <div className="flex gap-1 mt-2">
                        <div className="w-8 h-8 rounded-full bg-vexl-bg border-2 border-red-500/50"></div>
                        <div className="w-8 h-8 rounded-full bg-vexl-bg border-2 border-red-500/50 -ml-2"></div>
                        <div className="w-8 h-8 rounded-full bg-vexl-bg border-2 border-red-500/50 -ml-2"></div>
                        <div className="w-8 h-8 rounded-full bg-red-500/20 border-2 border-red-500/50 -ml-2 flex items-center justify-center text-xs text-red-400">+97</div>
                      </div>
                    </div>
                    <X className="w-6 h-6 text-red-400" />
                  </div>

                  {/* PDFs */}
                  <div className="flex items-start gap-4 p-4 bg-red-500/10 rounded-2xl border border-red-500/20">
                    <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">PDFs interminables</h4>
                      <p className="text-sm text-vexl-text-secondary">Rutinas en PDF que nadie abre, actualizaciones manuales, versiones desactualizadas...</p>
                      <div className="flex gap-2 mt-2">
                        <div className="px-2 py-1 bg-vexl-bg rounded text-xs text-red-400">rutina_v3_final.pdf</div>
                        <div className="px-2 py-1 bg-vexl-bg rounded text-xs text-red-400">rutina_v3_final_FINAL.pdf</div>
                      </div>
                    </div>
                    <X className="w-6 h-6 text-red-400" />
                  </div>

                  {/* Pagos */}
                  <div className="flex items-start gap-4 p-4 bg-red-500/10 rounded-2xl border border-red-500/20">
                    <div className="w-12 h-12 bg-yellow-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">Pagos por todos lados</h4>
                      <p className="text-sm text-vexl-text-secondary">Bizum, transferencia, PayPal, efectivo... ¿Quién ha pagado? ¿Quién debe?</p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <span className="px-2 py-1 bg-vexl-bg rounded text-xs text-yellow-400">Bizum</span>
                        <span className="px-2 py-1 bg-vexl-bg rounded text-xs text-blue-400">PayPal</span>
                        <span className="px-2 py-1 bg-vexl-bg rounded text-xs text-green-400">Transferencia</span>
                        <span className="px-2 py-1 bg-vexl-bg rounded text-xs text-gray-400">Efectivo</span>
                      </div>
                    </div>
                    <X className="w-6 h-6 text-red-400" />
                  </div>
                </div>

                <div className="mt-8 p-4 bg-red-500/10 rounded-xl border border-red-500/30 text-center">
                  <p className="text-red-400 font-semibold">Resultado: Caos total</p>
                  <p className="text-sm text-vexl-text-secondary mt-1">Pierdes clientes, tiempo y dinero</p>
                </div>
              </div>
            </div>

            {/* DESPUÉS - VEXL */}
            <div className="relative">
              <div className="absolute -top-4 left-4 bg-vexl-lime text-vexl-blue px-4 py-1 rounded-full text-sm font-bold z-10">
                CON VEXL.fit
              </div>
              <div className="bg-vexl-surface border-2 border-vexl-lime/30 rounded-3xl p-8 h-full">
                <div className="space-y-6">
                  {/* App Unificada */}
                  <div className="flex items-start gap-4 p-4 bg-vexl-lime/10 rounded-2xl border border-vexl-lime/20">
                    <div className="w-12 h-12 bg-vexl-lime rounded-xl flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-6 h-6 text-vexl-blue" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">Tu propia app con tu marca</h4>
                      <p className="text-sm text-vexl-text-secondary">Todos tus clientes en un solo lugar. Tu logo, tus colores, tu negocio.</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-vexl-lime text-sm font-medium">tumarca.vexl.fit</span>
                      </div>
                    </div>
                    <Check className="w-6 h-6 text-vexl-lime" />
                  </div>

                  {/* Rutinas Digitales */}
                  <div className="flex items-start gap-4 p-4 bg-vexl-lime/10 rounded-2xl border border-vexl-lime/20">
                    <div className="w-12 h-12 bg-[#1E45FB] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Play className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">Rutinas interactivas</h4>
                      <p className="text-sm text-vexl-text-secondary">Videos, cronómetros, seguimiento de progreso. Todo digital y automático.</p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="h-2 flex-1 bg-vexl-bg rounded-full overflow-hidden">
                          <div className="h-full w-3/4 bg-vexl-lime rounded-full"></div>
                        </div>
                        <span className="text-xs text-vexl-lime">75%</span>
                      </div>
                    </div>
                    <Check className="w-6 h-6 text-vexl-lime" />
                  </div>

                  {/* Pagos Automáticos */}
                  <div className="flex items-start gap-4 p-4 bg-vexl-lime/10 rounded-2xl border border-vexl-lime/20">
                    <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">Pagos con Stripe</h4>
                      <p className="text-sm text-vexl-text-secondary">Suscripciones automáticas directo a tu cuenta. Sin perseguir a nadie.</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-vexl-lime text-sm font-medium">+$4,500 este mes</span>
                      </div>
                    </div>
                    <Check className="w-6 h-6 text-vexl-lime" />
                  </div>
                </div>

                <div className="mt-8 p-4 bg-vexl-lime/10 rounded-xl border border-vexl-lime/30 text-center">
                  <p className="text-vexl-lime font-semibold">Resultado: Negocio escalable</p>
                  <p className="text-sm text-vexl-text-secondary mt-1">Gestiona 500 clientes como si fueran 5</p>
                </div>
              </div>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-3 bg-vexl-surface border border-vexl-border rounded-full px-6 py-3 mb-6">
              <img src="/logo.png" alt="VEXL.fit" className="h-12 w-auto" />
              <span className="text-white font-medium">Nos ocupamos de la gestión</span>
            </div>
            <h3 className="font-display text-3xl font-bold text-white mb-4">
              Para que tú te ocupes del <span className="text-vexl-lime">entreno</span>
            </h3>
            <button
              onClick={openWhatsApp}
              className="btn-primary text-lg px-8 py-4 mt-4"
            >
              <MessageCircle className="w-5 h-5 mr-2 inline" />
              Quiero mi plataforma
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-vexl-surface">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-vexl-lime/10 text-vexl-lime px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Features
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white">
              Todo lo que necesitas para<br />
              <span className="text-vexl-lime">escalar tu negocio</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Globe,
                title: "White-Label Engine",
                description: "Tu logo, tus colores, tu marca. Para tus seguidores, la app es 100% tuya.",
                color: "bg-vexl-blue"
              },
              {
                icon: Users,
                title: "CRM Inteligente",
                description: "Ve quién cumple la dieta, quién se va a dar de baja y tus métricas en tiempo real.",
                color: "bg-purple-600"
              },
              {
                icon: Zap,
                title: "Auto-Escalabilidad",
                description: "Gestiona 500 alumnos con el mismo esfuerzo que 5. El sistema hace el trabajo.",
                color: "bg-orange-500"
              },
              {
                icon: BarChart3,
                title: "Progress Tracking",
                description: "Fotos de transformación, gráficos de peso y rendimiento de cada alumno.",
                color: "bg-pink-500"
              },
              {
                icon: Shield,
                title: "Stripe Connect",
                description: "Pagos directos a tu cuenta. Sin intermediarios, comisiones transparentes.",
                color: "bg-emerald-500"
              },
              {
                icon: Smartphone,
                title: "App Móvil (PWA)",
                description: "Tus clientes instalan la app en su móvil. Sin App Store, sin complicaciones.",
                color: "bg-cyan-500"
              }
            ].map((feature, i) => (
              <div
                key={i}
                className="group card-dark p-8 hover:border-vexl-lime/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-vexl-text-secondary leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how" className="py-24 px-6 bg-vexl-bg">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-vexl-blue/20 text-[#1E45FB] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Cómo funciona
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white">
              3 pasos para lanzar<br />
              <span className="text-vexl-lime">tu imperio fitness</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Crea tu marca",
                description: "Sube tu logo, elige tus colores y conecta tu cuenta de Stripe para recibir pagos."
              },
              {
                step: "02",
                title: "Sube tu contenido",
                description: "Crea rutinas de entrenamiento, planes de nutrición y asigna a tus alumnos."
              },
              {
                step: "03",
                title: "Escala sin límites",
                description: "Comparte tu código de acceso y observa cómo crece tu comunidad automáticamente."
              }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="text-8xl font-display font-bold text-vexl-lime/10 absolute -top-8 left-0">
                  {item.step}
                </div>
                <div className="relative pt-12">
                  <h3 className="font-display text-2xl font-semibold text-white mb-4">
                    {item.title}
                  </h3>
                  <p className="text-vexl-text-secondary leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-vexl-blue relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-4xl mx-auto text-center">
          <img src="/logo.png" alt="VEXL.fit" className="h-24 w-auto mx-auto mb-8" />
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            ¿Listo para lanzar tu<br />
            <span className="text-vexl-lime">imperio fitness?</span>
          </h2>
          <p className="text-xl text-white/70 mb-10">
            Únete a los influencers que ya están escalando su negocio con VEXL.fit
          </p>
          <button
            data-testid="cta-btn"
            onClick={openWhatsApp}
            className="btn-primary text-lg px-10 py-5 flex items-center gap-3 mx-auto"
          >
            <MessageCircle className="w-6 h-6" />
            Solicitar Acceso por WhatsApp
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-vexl-bg border-t border-vexl-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="VEXL.fit" className="h-12 w-auto" />
          <p className="text-sm text-vexl-text-secondary">
            © 2026 VEXL.fit - Todos los derechos reservados
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
